import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SnackBarService } from 'src/app/core/services/snackbar.service';
import { environment } from 'src/environments/environment';

export type ConnectionStatus = 'connected' | 'disconnected' | 'noInternet';

/** Resultado de diagnosticar por qué falló una petición al backend */
export type ConnectionDiagnosis = 'online' | 'noInternet' | 'serverDown';

@Injectable({providedIn: 'root'})
export class ConnectionService {
  private notification = inject(SnackBarService);

  private onlineStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(navigator.onLine);
  public onlineStatus$ = this.onlineStatus.asObservable();

  /** Tiempo máximo de espera de cada sondeo */
  private readonly PROBE_TIMEOUT_MS = 5000;

  /** Sitios externos de alta disponibilidad para comprobar si hay internet */
  private readonly INTERNET_PROBES = [
    'https://www.google.com/generate_204',
    'https://www.cloudflare.com/cdn-cgi/trace'
  ];

  /** Diagnóstico en curso: varias peticiones que fallan a la vez comparten uno solo */
  private pendingDiagnosis: Promise<ConnectionDiagnosis> | null = null;

  get isConnected() {
    return this.onlineStatus.value;
  }

  constructor() {
    window.addEventListener('online', () => this.connected());
    window.addEventListener('offline', () => this.disconnected());
  }

  public showToastError() {
    this.notification.renderConnection('noInternet', 'adp-danger-snackbar')
  }

  /**
   * Averigua si una petición fallida se debe al servidor o a la conexión del usuario:
   * primero el health check del backend; si no responde, un sitio externo.
   */
  public diagnose(): Promise<ConnectionDiagnosis> {
    if (!this.pendingDiagnosis) {
      this.pendingDiagnosis = this.runDiagnosis().finally(() => this.pendingDiagnosis = null);
    }
    return this.pendingDiagnosis;
  }

  private async runDiagnosis(): Promise<ConnectionDiagnosis> {
    if (!navigator.onLine) return 'noInternet';

    // El backend permite CORS, así que se puede leer el estado real del health check
    const serverUp = await this.probe(`${environment.app_urls.source}/`, 'cors');
    if (serverUp) return 'online';

    const results = await Promise.all(this.INTERNET_PROBES.map(url => this.probe(url, 'no-cors')));
    return results.some(Boolean) ? 'serverDown' : 'noInternet';
  }

  /**
   * Devuelve true si la URL respondió desde la red.
   * Se usa fetch (no HttpClient) para no pasar por los interceptores, y `ngsw-bypass`
   * para que el service worker no responda por su cuenta con un 504 estando sin conexión.
   */
  private async probe(url: string, mode: RequestMode): Promise<boolean> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.PROBE_TIMEOUT_MS);
    try {
      const probeUrl = new URL(url);
      probeUrl.searchParams.set('ngsw-bypass', 'true');
      const response = await fetch(probeUrl.toString(), { mode, cache: 'no-store', signal: controller.signal });
      // En no-cors una respuesta real de la red es opaca; cualquier otra la fabricó el service worker
      return mode === 'no-cors' ? response.type === 'opaque' : response.ok;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  }

  private connected() {
    this.notification.renderConnection('connected', 'adp-success-snackbar')
    this.onlineStatus.next(true);
  }

  private disconnected() {
    this.notification.renderConnection('disconnected', 'adp-warning-snackbar')
    this.onlineStatus.next(false);
  }
}
